// The `evidence/library` source-list WRITE hooks — carved from
// `src/components/library/source-lists/hooks/useSourceLists.ts` (now a
// delegate) at D2 slice 1, ARCFE-1088.
//
// TWO OF THE FOUR WRITES MOVED, AND THE PARTITION IS THE POINT. The family
// record has said since ARCFE-1069 that `useCreateSourceList` and
// `useUpdateSourceList` "are already headless and are the obvious next slice";
// this is that slice, taken at its stated size rather than widened.
//
// Re-measured at HEAD before moving rather than inherited from that sentence:
// between them these two hooks import `useMutation`, `useQueryClient`, the two
// frozen keys, the two request types and the two api fns — and nothing else.
// No toast, no atom, no store read, no flag, no intl. The severance cost is
// literally zero, which is why they move without a port being invented; a port
// with no effect behind it is surface, not progress.
//
// WHAT DID NOT MOVE, AND WHY EACH IS A DECISION RATHER THAN A DEFERRAL:
//
//   `useDeleteSourceList` — the trio the family record names. It toasts
//   (`@heidi/design-system/toast`) with an INTL-resolved title
//   (`t('library.deleteSourceListError')`), and on success it writes
//   `sourceControlToggleAtom` from `@/atoms/chatAtoms`. The toast half is the
//   ordinary app-reaction recipe and would cross as an `onDeleteError` port.
//   The ATOM half is not: `chatAtoms` is inside the B0 chat quarantine, and
//   this hook mutating chat's source-selection state IS the `chatAtoms`
//   data-layer edge that task 3.4 has flagged since ARCFE-1044 as "needs a
//   real chat ruling, not a UI deferral". Porting it as an `onDeleted`
//   callback would relocate the file without settling the ruling and would let
//   the family bank a Green it has not earned. It stays whole until chat is
//   ruled. The T1 `meta` escape was re-checked against meta v1.1 (ARCFE-1082,
//   #18879), which admits a legacy `{ i18nextKey }` as a `ToastCopySource` —
//   so the intl title alone no longer forces T2, and the earlier draft of this
//   comment that said it did was wrong. The toast is still T2 for a different
//   reason: its description is `error.message`, a computed runtime value, and
//   `appendBackendDetail` is not a substitute (it splices
//   `extractAxiosErrorDetail(error)`, and with no usable detail it leaves the
//   base copy unchanged — no description at all where one shows today). And
//   none of that is load-bearing anyway: no toast mechanism moves an atom
//   write or the optimistic-delete pair.
//
//   `useImportSourceList` — excluded by ROUTE, exactly as its transport
//   `importSourceList` already is (see `data/api/sourceLists.ts`). It POSTs to
//   `/evidence/collections:import-source-list/{id}`, returns
//   `CollectionDetailResponse` from `@/components/library/types`, and
//   invalidates `COLLECTIONS_QUERY_KEY` — a key this package's `/contracts`
//   holds but this family does not own. It is collections' hook; it lands with
//   whoever opens collections.
//
// Behaviour is preserved to the character: same keys, same `setQueryData`
// optimistic add, same invalidation set in the same order, same `void` on the
// floating promises. The only thing that changed is which file the body is in.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  SOURCE_LISTS_QUERY_KEY,
  SOURCE_LIST_DETAIL_QUERY_KEY,
  type CreateSourceListRequest,
  type SourceList,
  type UpdateSourceListRequest,
} from '../../contracts';
import { createSourceList, updateSourceList } from '../api';

/**
 * Hook to create a new source list
 */
export function useCreateSourceList() {
  const queryClient = useQueryClient();

  return useMutation<SourceList, Error, CreateSourceListRequest>({
    mutationFn: createSourceList,
    onSuccess: (newList) => {
      // Optimistically add to cache so the scrub effect in useEvidenceSourceConfig
      // doesn't remove the newly selected ID before the refetch completes.
      queryClient.setQueryData<SourceList[]>([SOURCE_LISTS_QUERY_KEY], (old) =>
        old ? [...old, newList] : [newList]
      );
      void queryClient.invalidateQueries({ queryKey: [SOURCE_LISTS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update a source list
 */
export function useUpdateSourceList() {
  const queryClient = useQueryClient();

  return useMutation<
    SourceList,
    Error,
    { sourceListId: string; data: UpdateSourceListRequest }
  >({
    mutationFn: ({ sourceListId, data }) => updateSourceList(sourceListId, data),
    onSuccess: (_, { sourceListId }) => {
      void queryClient.invalidateQueries({ queryKey: [SOURCE_LISTS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [SOURCE_LIST_DETAIL_QUERY_KEY, sourceListId],
      });
    },
  });
}
